<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <ModuleShell
    :title="$t('views.selectedGoat.generalInfo.editTitle')"
    :icon="Pencil"
    hideAction
  >
  <!--Form for Editing general Goat Information-->
    <form v-if="goat" class="space-y-4" @submit.prevent="submitForm">

      <!-- Ear Tag -->
      <div class="flex flex-col">
        <label class="font-semibold mb-1">
          {{ $t('views.goats.goatForm.eartag.label') }}
        </label>
        <input
          v-model="form.eartag"
          type="text"
          :placeholder="$t('views.goats.goatForm.eartag.placeholder')"
          @input="handleEartagInput"
          maxlength="17"
          class="w-full p-3 rounded-lg border border-[var(--color-primary-400)] bg-[var(--color-primary-100)] text-[var(--color-text)] font-mono text-lg tracking-wider focus:outline-none focus:ring-2 focus:ring-[var(--color-highlight)]"
        />
        <!-- Eartag format help -->
        <span class="text-xs text-[var(--color-text)] mt-1">
          {{ $t('views.goats.goatForm.eartag.formatHelper') }}
        </span>
        <!-- Live status -->
        <div v-if="form.eartag && isValidEartagFormat(form.eartag) && !validationErrors.eartag" class="flex items-center gap-2 text-green-600 text-sm mt-1">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
          </svg>
          <span>{{ $t('views.goats.goatForm.eartag.valid') }}</span>
        </div>
        <!-- Error Message -->
        <span
          v-if="
            (validationErrors.eartag === $t('views.goats.goatForm.eartag.duplicate')) ||
            (submitted && validationErrors.eartag === $t('views.goats.goatForm.eartag.invalid'))
          "
          class="text-red-500 text-xs mt-2 block">
          {{ validationErrors.eartag }}
        </span>
      </div>

      <!-- Name -->
      <div class="flex flex-col">
        <label class="font-semibold mb-1">{{ $t('views.goats.goatForm.name.label') }}</label>
        <input
          v-model="form.name"
          type="text"
          :placeholder="$t('views.goats.goatForm.name.placeholder')"
          class="w-full p-3 rounded-lg border border-[var(--color-primary-400)] bg-[var(--color-primary-100)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-highlight)]"
        />
      </div>

      <!-- Date of Birth -->
      <div class="flex flex-col">
        <label class="font-semibold mb-1">
          {{ $t('views.goats.goatForm.dateOfBirth.label') }} *
        </label>
        <input
          v-model="form.dateOfBirth"
          type="date"
          required
          :max="new Date().toISOString().split('T')[0]"
          class="w-full p-3 rounded-lg border border-[var(--color-primary-400)] bg-[var(--color-primary-100)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-highlight)]"
        />
        <span
          v-if="validationErrors.dateOfBirth"
          class="text-red-500 text-xs mt-1">
          {{ validationErrors.dateOfBirth }}
        </span>
      </div>

      <!-- Place of Birth -->
      <div class="flex flex-col">
        <label class="font-semibold mb-1">
          {{ $t('views.goats.goatForm.placeOfBirth.label') }}
        </label>
        <input
          v-model="form.placeOfBirth"
          type="text"
          :placeholder="$t('views.goats.goatForm.placeOfBirth.placeholder')"
          class="w-full p-3 rounded-lg border border-[var(--color-primary-400)] bg-[var(--color-primary-100)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-highlight)]"
        />
      </div>

      <!-- Breed -->
      <div class="flex flex-col">
        <label class="font-semibold mb-1">{{ $t('views.goats.goatForm.breed.label') }}</label>
        <select v-model="form.breed" class="dropdown-select w-full p-3 pr-10 rounded-lg border border-[var(--color-primary-400)] bg-[var(--color-primary-100)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-highlight)]">
          <option value="" disabled hidden>{{ $t('views.goats.goatForm.breed.selectbreed') }}</option>
          <option :value="null">{{ $t('views.goats.goatForm.breed.noBreed') }}</option>
          <option value="Burenziege">{{ $t('views.goats.goatForm.breed.boerGoat') }}</option>
          <option value="Thüringer Waldziege">{{ $t('views.goats.goatForm.breed.thuringianGoat') }}</option>
        </select>
      </div>

      <!-- Gender -->
      <div class="flex flex-col">
        <label class="font-semibold mb-1">
          {{ $t('views.goats.goatForm.gender') }} *
        </label>
        <select v-model="form.gender" required class="dropdown-select w-full p-3 pr-10 rounded-lg border border-[var(--color-primary-400)] bg-[var(--color-primary-100)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-highlight)]">
          <option value="Female">{{ $t('views.goats.goatForm.female') }}</option>
          <option value="Male">{{ $t('views.goats.goatForm.male') }}</option>
        </select>
      </div>

      <!-- Group -->
      <div class="flex flex-col">
        <label class="font-semibold mb-1">
          {{ $t('views.goats.goatForm.group') }}
        </label>
        
        <!-- Loading State -->
        <div
          v-if="groupsLoading"
          class="w-full p-3 rounded-lg border border-[var(--color-primary-400)] text-center opacity-70 bg-[var(--color-primary-100)] text-[var(--color-text)]">
          <span class="text-sm">Loading groups...</span>
        </div>

        <!-- Dropdown -->
        <select
          v-else
          v-model="form.groupId"
          class="dropdown-select w-full p-3 pr-10 rounded-lg border border-[var(--color-primary-400)] bg-[var(--color-primary-100)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-highlight)]">
          <option :value="null">
            {{ $t('views.goats.goatForm.noGroup') }}
          </option>
          <option
            v-for="group in availableGroups"
            :key="group.id"
            :value="group.id">
            {{ group.name }}{{ group.location ? ` - ${group.location}` : '' }}
          </option>
        </select>
        <!-- <p class="text-xs opacity-60 mt-1">
          {{ $t('views.goats.goatForm.groupHelpText') }}
        </p> -->
      </div>

      <button type="submit" class="btn btn--primary w-full py-3 text-lg">
        {{ $t('common.buttons.save') }}
      </button>

    </form>

    <div v-else class="text-center opacity-50 py-6">
      {{ $t('views.selectedGoat.loading') }}
    </div>
  </ModuleShell>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import { Pencil } from 'lucide-vue-next'
import ModuleShell from '@/components/ui/layout/ModuleShell.vue'
import { useGroupStore } from '@/stores/group.store.js'
import { useGoatStore } from '@/stores/goat.store.js'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'

const emit = defineEmits(['save'])

const props = defineProps({
  goat: { type: Object, default: null }
})

// Stores
const groupStore = useGroupStore()
const goatStore = useGoatStore()
const { t } = useI18n()
const { groups: availableGroups, loading: groupsLoading } = storeToRefs(groupStore)
const { fetchGroups } = groupStore

// Reactive form object
const form = ref({
  eartag: '',
  name: '',
  dateOfBirth: '',
  placeOfBirth: '',
  breed: '',
  gender: 'Female',
  groupId: null
})

// Validation state
const validationErrors = ref({})
const submitted = ref(false)

/**
 * Format eartag as DE XXXX XXX XXXXX (4-3-5 digit pattern)
 */
function formatEartag(input) {
  // Remove all spaces and convert to uppercase
  const cleaned = input.replace(/\s/g, '').toUpperCase()
  
  // Limit to DE + 12 digits
  const limited = cleaned.substring(0, 14) // DE + 12 digits
  
  // Build formatted string
  let formatted = ''
  
  // Add "DE" prefix (first 2 chars)
  if (limited.length > 0) {
    formatted = limited.substring(0, 2)
  }
  
  // Add remaining digits with specific spacing: DE XXXX XXX XXXXX
  if (limited.length > 2) {
    const digits = limited.substring(2)
    formatted += ' '
    
    // First 4 digits
    for (let i = 0; i < Math.min(4, digits.length); i++) {
      formatted += digits[i]
    }
    
    // Space before next 3 digits
    if (digits.length > 4) {
      formatted += ' '
      for (let i = 4; i < Math.min(7, digits.length); i++) {
        formatted += digits[i]
      }
    }
    
    // Space before last 5 digits
    if (digits.length > 7) {
      formatted += ' '
      for (let i = 7; i < digits.length; i++) {
        formatted += digits[i]
      }
    }
  }
  
  return formatted
}

/**
 * Validate full eartag format - must be DE followed by exactly 12 digits
 */
function isValidEartagFormat(eartag) {
  if (!eartag) return false;
  const cleaned = eartag.replace(/\s/g, '');
  const pattern = /^DE\d{12}$/;
  return pattern.test(cleaned);
}

/**
 * Check for duplicate eartag in existing goats (excluding current goat)
 */
function isDuplicateEartag(eartag) {
  const cleaned = eartag.replace(/\s/g, '');
  return goatStore.goats.some(g => 
    g.eartag && 
    g.eartag.replace(/\s/g, '') === cleaned && 
    g.id !== props.goat?.id // Exclude the current goat being edited
  );
}

/**
 * Handle eartag input with auto-formatting and live validation
 */
function handleEartagInput(event) {
  const input = event.target.value;
  const formatted = formatEartag(input);
  form.value.eartag = formatted;
  
  // Only show duplicate error live, not format error
  if (isDuplicateEartag(formatted)) {
    validationErrors.value.eartag = t('views.goats.goatForm.eartag.duplicate');
  } else {
    // Only clear error if it was duplicate or format error
    if (
      validationErrors.value.eartag === t('views.goats.goatForm.eartag.duplicate') ||
      validationErrors.value.eartag === t('views.goats.goatForm.eartag.invalid')
    ) {
      delete validationErrors.value.eartag;
    }
  }
}

/**
 * Validate form data
 */
function validateForm() {
  validationErrors.value = {};
  let isValid = true;

  // Eartag optional - but validate format and uniqueness if provided
  if (form.value.eartag && form.value.eartag.trim()) {
    if (!isValidEartagFormat(form.value.eartag)) {
      validationErrors.value.eartag = t('views.goats.goatForm.eartag.invalid');
      isValid = false;
    } else if (isDuplicateEartag(form.value.eartag)) {
      validationErrors.value.eartag = t('views.goats.goatForm.eartag.duplicate');
      isValid = false;
    }
  }

  // Date of birth validation
  if (!form.value.dateOfBirth) {
    validationErrors.value.dateOfBirth = t('views.goats.goatForm.required');
    isValid = false;
  } else {
    const birthDate = new Date(form.value.dateOfBirth);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    birthDate.setHours(0, 0, 0, 0);
    if (birthDate > today) {
      validationErrors.value.dateOfBirth = t('views.goats.goatForm.dateInFuture');
      isValid = false;
    }
  }

  return isValid;
}

// Fetch groups on mount
onMounted(async () => {
  if (availableGroups.value.length === 0) {
    await fetchGroups()
  }
})

// Watch goat and initialize form safely
watch(
  () => props.goat,
  (g) => {
    if (!g) return
    form.value = {
      eartag: g.eartag || '',
      name: g.name || '',
      dateOfBirth: g.dateOfBirth || '',
      placeOfBirth: g.placeOfBirth || '',
      breed: g.breed || '',
      gender: g.gender || 'Female',
      groupId: g.groupId || null
    }
  },
  { immediate: true }
)

function submitForm() {
  submitted.value = true
  
  if (!validateForm()) {
    return
  }
  
  emit('save', { ...form.value })
}
</script>
<!--TMP stored css-->
<style scoped>
.input {
  width: 100%;
  background-color: #ffffff;
  color: #000000;
  border: 1px solid var(--color-primary-400);
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  transition: all 0.2s ease;
}

.input:focus {
  outline: none;
  border-color: var(--color-success);
  box-shadow: 0 0 0 2px var(--color-success);
}

.input-lg {
  font-size: 1.125rem;
  padding: 1rem 1.25rem;
}

.btn--primary {
  background-color: var(--color-success);
  border-color: var(--color-success);
  color: #fff;
  font-weight: 700;
  transition: all 0.2s ease;
}

.btn--primary:hover {
  background-color: var(--color-success-dark);
  border-color: var(--color-success-dark);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.btn--primary:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
</style>
